import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Compass, ListChecks, LogOut, Settings as SettingsIcon, User } from 'lucide-react';
import { useAuth } from '../auth/useAuth';
import { useWorkspace } from '../workspace/WorkspaceProvider';
import { accountDisplayName, accountInitials } from '../workspace/userName';
import { useTour } from '../tour/TourProvider';
import { useClientFrame } from '../advisor/useClientFrame';
import { APP_ENV, APP_VERSION, versionLabel } from '../lib/appEnv';
import { UserText } from './UserText';

const ICON_STROKE = 1.75;

/**
 * Account menu for the top app bar (nav-IA §4.4, relocated 2026-07-04 from the
 * sidebar footer to the top-right — identity is app-agnostic shell chrome). The
 * avatar button opens a downward, right-aligned menu: Getting started, Take the
 * tour, then Sign out. Settings moved to the sidebar's bottom (2026-07-07) and
 * Feedback followed (2026-07-13) — both configure or address the app itself, not
 * just the signed-in user, so they live with the app's nav. On mobile the sidebar
 * IS the hamburger drawer, so Feedback rides there alongside Settings.
 *
 * **Getting started and Take the tour are both suppressed in the advisor firm's own
 * workspace** (steward-app#1541). Matt: "Getting Started doesn't work for firms. It
 * should just be removed from the user menu until we make it work" — and the Tour
 * inherits the same answer, since its own step 1 assumes a Portfolio pillar a firm
 * workspace does not have (see `tour/steps.ts`'s `nav` step). Neither has a URL of its
 * own to guard defensively: Getting started deep-links `/?onboarding=1`, and `/` in a
 * firm workspace ordinarily never reaches `Dashboard` (the card's only host) regardless
 * of the query string — `HomeRoute` redirects to `/advisor` or `FirmWorkspaceNotReady`
 * first, see that file's own header. The tour has no route at all; this button (plus
 * `WelcomeOverlay`'s arrival offer, already gated by `shouldShowWelcome`) is its only
 * entry point. So hiding these two buttons is the whole fix, not half of one — for the
 * ordinary case.
 *
 * That "ordinarily" carries the same residual `Settings.tsx` already names for the
 * Advisors tab's gate (read that file's header before touching this one): a firm
 * workspace that has not mounted the `steward-advisor-firm` pack is reachable and
 * permanent, not just a loading window (`useWorkspaceKind`'s own header, steward-app#736).
 * There, `inFirmWorkspace` reads `false` until this caller's own `advisorFor`/`firmChoice`
 * resolves — same predicate this component reads off `useClientFrame` — so both buttons
 * ARE offered, and `HomeRoute`'s `/advisor` redirect needs the same `advisorFor`/`firmChoice`
 * and so does not fire either; `HomeRoute` DOES still fall to `FirmWorkspaceNotReady` off
 * `workspaceKind === 'firm'` alone once the pack mounts, which is the ordinary case above,
 * but the whole point of #736 is that it can stay unmounted. So in that unmounted, unresolved
 * window, `/?onboarding=1` can reach `Dashboard` after all. The gate here is correct as
 * written — it is the same predicate every other #1541/#808 consumer uses, deliberately, and
 * narrowing it further is not this component's fix to make — steward-app#736 is where the
 * residual closes, for every consumer of this predicate at once.
 *
 * **`bare` (steward-app#1828):** the variant `Layout.tsx`'s `BareTopBar` renders for a
 * memberless account (`/me` loaded, no active membership). Getting started and Take the
 * tour drop out — same reasoning as the firm-workspace case above, neither is built for
 * an account with no workspace to start or tour — and an **Account settings** item takes
 * their place, linking to `/settings/profile`. Memberless accounts keep Settings access on
 * purpose (decided, not incidental): `MEMBERLESS_ALLOWED_PATHS` (`WorkspaceProvider.tsx`)
 * already allows the identity-scoped Settings tabs through, and the first-run
 * "account already exists" message sends a locked-out person there — this is their only
 * way in now that the sidebar's own Settings link is gone. Sign out and the version
 * footer are unchanged; only the guidance group above the separator differs.
 */
export function AccountMenu({ bare = false }: { bare?: boolean } = {}) {
  const { signOut } = useAuth();
  const { profile } = useWorkspace();
  const navigate = useNavigate();
  const { start: startTour } = useTour();
  const { inFirmWorkspace } = useClientFrame();
  const displayName = accountDisplayName(profile);
  const initials = accountInitials(profile);
  // Show the email as a secondary line only when we have a real name above it.
  const secondary = profile?.name?.trim() && profile.email && profile.email !== profile.name ? profile.email : null;
  const [menuOpen, setMenuOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setMenuOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  const menuItemClass =
    'flex w-full items-center gap-1.5 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-slate no-underline font-sans bg-transparent border-none cursor-pointer text-left transition-colors duration-150 hover:text-navy hover:bg-cream';

  return (
    <div ref={ref} className="relative" data-tour="account-menu">
      <button
        onClick={() => setMenuOpen(o => !o)}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        aria-label={`Account menu for ${displayName}`}
        className="group flex items-center gap-2 max-sm:min-h-11 max-sm:min-w-11 max-sm:justify-center rounded-token bg-transparent border-none cursor-pointer px-1.5 py-1 transition-colors duration-150 hover:bg-cream"
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-token bg-navy/8 font-sans text-[11px] font-bold tracking-[0.04em] text-navy">
          {initials || <User size={15} strokeWidth={ICON_STROKE} aria-hidden />}
        </span>
        {/* Name is hidden on narrow viewports — avatar-only, matching the former rail behavior. */}
        <span className="hidden min-w-0 max-w-[160px] text-left sm:block">
          <span className="block truncate font-sans text-[12px] font-medium text-navy"><UserText>{displayName}</UserText></span>
          {secondary && <span className="block truncate font-sans text-[10px] text-slate"><UserText>{secondary}</UserText></span>}
        </span>
        <ChevronDown size={15} strokeWidth={ICON_STROKE} aria-hidden className="hidden shrink-0 text-slate sm:block" />
      </button>
      {menuOpen && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-1 z-50 min-w-[176px] rounded-token border border-line bg-surface py-1.5 shadow-[0_10px_30px_-10px_rgba(15,23,42,0.15)]"
        >
          {/* Guidance group — reopens the first-run checklist (FR4) and the tour.
              Feedback is no longer here: it's a full page in the sidebar (which is
              the hamburger drawer on mobile), so there's one feedback path per
              viewport without duplicating it in the identity menu.
              Both suppressed in the advisor firm's own workspace (steward-app#1541,
              see this component's own header) — neither is built for a workspace
              with no family pillars in it. */}
          {!bare && !inFirmWorkspace && (
            <button
              role="menuitem"
              onClick={() => { setMenuOpen(false); navigate('/?onboarding=1'); }}
              className={menuItemClass}
            >
              <ListChecks size={15} strokeWidth={ICON_STROKE} aria-hidden />
              Getting started
            </button>
          )}
          {!bare && !inFirmWorkspace && (
            <button
              role="menuitem"
              onClick={() => { setMenuOpen(false); startTour(); }}
              className={menuItemClass}
            >
              <Compass size={15} strokeWidth={ICON_STROKE} aria-hidden />
              Take the tour
            </button>
          )}
          {/* steward-app#1828 — the bare shell has no sidebar Settings link, so this is a
              memberless account's only door to Settings. `/settings/profile` directly
              (not the bare `/settings` index it would redirect to anyway): naming the
              destination here matches what a locked-out person was already told to do
              (the first-run "account already exists" copy names Settings by name). */}
          {bare && (
            <button
              role="menuitem"
              onClick={() => { setMenuOpen(false); navigate('/settings/profile'); }}
              className={menuItemClass}
            >
              <SettingsIcon size={15} strokeWidth={ICON_STROKE} aria-hidden />
              Account settings
            </button>
          )}

          <div role="separator" className="my-1 border-t border-line" />

          <button
            role="menuitem"
            onClick={() => { setMenuOpen(false); void signOut(); }}
            className={menuItemClass}
          >
            <LogOut size={15} strokeWidth={ICON_STROKE} aria-hidden />
            Sign out
          </button>

          {/* Release version — non-interactive footer. Subtle, present in every env
              (incl. prod) so a user can read it back on a call. `title` carries the
              env + the full untrimmed version for quick inspection.
              Spelled "Version:" rather than a bare "v": support has to be able to
              ASK for this over the phone, and a lone "v" beside a date-shaped string
              names nothing the user can find again. Deliberately unchanged in size,
              weight, and color — legibility is the fix, not prominence. */}
          <div role="separator" className="my-1 border-t border-line" />
          <div
            className="px-4 py-1.5 font-sans text-[10px] text-slate select-text"
            title={`${APP_ENV} · ${APP_VERSION}`}
          >
            Version: {versionLabel()}
          </div>
        </div>
      )}
    </div>
  );
}
